import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  ThemeProvider,
  useTheme,
  Typography,
  Box,
  Divider,
  Chip,
  FormHelperText,
} from "@mui/material";
import { Add, Close, Remove, Send } from "@mui/icons-material";
import { useContext, useMemo, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { InputTheme } from "../../themes/InputTheme";
import { ManagedAccountContext } from "../../context/ManagedAccountContext";

const WSU_RED = "#A60F2D";

const labelCellSx = {
  fontWeight: 600,
  color: "#444",
  width: "35%",
  fontSize: "0.9rem",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const UpdateManagedAccount = () => {
  const { authState } = useOktaAuth();
  const { managedAccountModule, setManagedAccountModule } = useContext(ManagedAccountContext);
  const { setModule } = useContext(ModuleContext);
  const outerTheme = useTheme();

  // Captured once at mount — never changes
  const originalManager = managedAccountModule.data["manager"] ?? "";

  // If the DN contains OU=Shared, delegates are referred to as Full Access
  const isSharedMailbox = managedAccountModule.dn?.includes("OU=Shared");
  const delegateLabel = isSharedMailbox ? "Full Access Granted" : "Delegates";

  const [state, setState] = useState({
    isLoaded: true,
    error: null,
    message: null,
    comment: "",
    displayName: managedAccountModule.data["displayName"] || "",
    telephoneNumber: managedAccountModule.data["telephoneNumber"] || "",
    department: managedAccountModule.data["department"] || "",
    manager: originalManager,
    name: managedAccountModule.data["name"] || "",
    delegates: [...(managedAccountModule.data["delegates"] || [])],
    proxyAddresses: [...(managedAccountModule.data["proxyAddresses"] || [])],
  });

  const [proposedDelegate, setProposedDelegate] = useState("");
  const [proposedProxyAddress, setProposedProxyAddress] = useState("");

  const phoneRegex = /^(\+?\d{1,2}\s?)?(\(?\d{3}\)?[\s-]?)?\d{3}[\s-]?\d{4}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const isPhoneInvalid = !!state.telephoneNumber.trim() && !phoneRegex.test(state.telephoneNumber.trim());
  const isManagerInvalid = !!state.manager.trim() && !emailRegex.test(state.manager.trim());

  // Manager has changed only if the current value differs from the original
  // and the new value is a non-empty, valid email
  const managerChanged =
      state.manager.trim() !== originalManager.trim() &&
      state.manager.trim() !== "" &&
      emailRegex.test(state.manager.trim());

  const isFormValid = useMemo(() => {
    return (
        state.displayName.trim() &&
        state.name.trim() &&
        (!state.telephoneNumber.trim() || phoneRegex.test(state.telephoneNumber.trim())) &&
        state.manager.trim() &&
        emailRegex.test(state.manager.trim()) &&
        state.comment.trim()
    );
  }, [state]);

  const addDelegate = () => {
    if (proposedDelegate) {
      setState((prev) => ({
        ...prev,
        delegates: [...prev.delegates, proposedDelegate],
      }));
      setProposedDelegate("");
    }
  };

  const removeDelegate = (delegateToRemove) => {
    setState((prev) => ({
      ...prev,
      delegates: prev.delegates.filter((d) => d !== delegateToRemove),
    }));
  };

  const updateManagedAccount = async () => {
    setState((prev) => ({ ...prev, isLoaded: false, error: null }));

    const trimmedProxy = proposedProxyAddress.trim();
    const updatedProxies = trimmedProxy
        ? [...state.proxyAddresses, trimmedProxy]
        : [...state.proxyAddresses];

    try {
      const response = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/managed-accounts/update`,
          {
            method: "POST",
            headers: {
              Authorization: authState.accessToken.accessToken,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              dn: managedAccountModule.dn,
              userPrincipalName: managedAccountModule.upn,
              displayName: state.displayName,
              name: state.name,
              telephoneNumber: state.telephoneNumber,
              department: state.department,
              manager: state.manager,
              // Only include previousManager if the manager genuinely changed
              // and the original was a real value (not empty)
              ...(managerChanged && originalManager && { previousManager: originalManager }),
              delegates: state.delegates,
              proxyAddresses: updatedProxies,
              callerId: authState.accessToken.claims.sub,
              comment: state.comment,
            }),
          }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || "Update failed");
      }

      const result = await response.json();
      setManagedAccountModule((prev) => ({
        ...prev,
        data: { ...managedAccountModule.data, ...result },
      }));

      setState((prev) => ({
        ...prev,
        proxyAddresses: updatedProxies,
        isLoaded: true,
        message: "AD updates have been completed. Please wait 5 minutes for Exchange Online updates to complete.",
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoaded: true,
        error: error.message || "Unknown error",
      }));
    }
  };

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.message) {
    return (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <TableContainer component={Paper} elevation={2} sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Update Complete
              </Typography>
            </Box>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {state.message}
              </Typography>
            </Box>
            <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
              <Button
                  variant="outlined"
                  endIcon={<Close />}
                  onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
                  sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
              >
                Close
              </Button>
            </Box>
          </TableContainer>
        </Box>
    );
  }

  return (
      <ThemeProvider theme={InputTheme(outerTheme)}>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <TableContainer component={Paper} elevation={2} sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>

            {/* Error Banner */}
            {state.error && (
                <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Update failed
                  </Typography>
                  <Typography variant="body2">{state.error}</Typography>
                </Box>
            )}

            {/* ── Section: Account Details ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Account Details
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
                {managedAccountModule.upn}
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Display Name *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.displayName}
                        onChange={(e) => setState((prev) => ({ ...prev, displayName: e.target.value }))}
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Name *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.name}
                        onChange={(e) => setState((prev) => ({ ...prev, name: e.target.value }))}
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Contact & Organization ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Contact &amp; Organization
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Telephone Number</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.telephoneNumber}
                        onChange={(e) => setState((prev) => ({ ...prev, telephoneNumber: e.target.value }))}
                        error={isPhoneInvalid}
                        sx={fieldSx}
                    />
                    {isPhoneInvalid && (
                        <FormHelperText error>Please enter a valid phone number</FormHelperText>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Department</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.department}
                        onChange={(e) => setState((prev) => ({ ...prev, department: e.target.value }))}
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Manager *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.manager}
                        onChange={(e) => setState((prev) => ({ ...prev, manager: e.target.value }))}
                        error={isManagerInvalid}
                        placeholder="manager@wsu.edu"
                        sx={fieldSx}
                    />
                    {isManagerInvalid && (
                        <FormHelperText error>Please enter a valid email address</FormHelperText>
                    )}
                    {managerChanged && originalManager && (
                        <FormHelperText sx={{ color: "#777" }}>
                          Previous manager: {originalManager}
                        </FormHelperText>
                    )}
                    {managerChanged && !originalManager && (
                        <FormHelperText sx={{ color: "#777" }}>
                          No previous manager on record
                        </FormHelperText>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Delegates ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                {delegateLabel}
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Add {delegateLabel}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                          fullWidth
                          size="small"
                          value={proposedDelegate}
                          onChange={(e) => setProposedDelegate(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && proposedDelegate) addDelegate(); }}
                          placeholder="Enter delegate name or email"
                          sx={fieldSx}
                      />
                      <Button
                          variant="contained"
                          onClick={addDelegate}
                          disabled={!proposedDelegate}
                          sx={{
                            backgroundColor: WSU_RED,
                            color: "white",
                            minWidth: 44,
                            px: 1.5,
                            "&:hover": { backgroundColor: "#8a0c25" },
                            "&.Mui-disabled": { backgroundColor: "#ddd" },
                          }}
                      >
                        <Add />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {state.delegates.length > 0 ? (
                <Box sx={{ px: 3, py: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {state.delegates.map((delegate) => (
                      <Chip
                          key={delegate}
                          label={delegate}
                          onDelete={() => removeDelegate(delegate)}
                          deleteIcon={<Remove fontSize="small" />}
                          sx={{
                            backgroundColor: "#fdf0f2",
                            border: `1px solid ${WSU_RED}`,
                            color: WSU_RED,
                            fontWeight: 500,
                            "& .MuiChip-deleteIcon": {
                              color: WSU_RED,
                              "&:hover": { color: "#8a0c25" },
                            },
                          }}
                      />
                  ))}
                </Box>
            ) : (
                <Box sx={{ px: 3, py: 1.5 }}>
                  <Typography variant="body2" color="text.disabled" fontStyle="italic">
                    No {delegateLabel.toLowerCase()} added
                  </Typography>
                </Box>
            )}

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Mail ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Mail
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Additional Proxy Address</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={proposedProxyAddress}
                        onChange={(e) => setProposedProxyAddress(e.target.value)}
                        placeholder="e.g. alias@wsu.edu"
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Justification ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Justification
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Comment *</TableCell>
                  <TableCell>
                    <TextField
                        required
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        value={state.comment}
                        onChange={(e) => setState((prev) => ({ ...prev, comment: e.target.value }))}
                        placeholder="Provide a reason for updating this account"
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ── Submit / Cancel ── */}
            <Box sx={{ px: 3, pt: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                  variant="outlined"
                  endIcon={<Close />}
                  onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
                  sx={{
                    borderColor: "#ccc",
                    color: "#666",
                    "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" },
                  }}
              >
                Cancel
              </Button>
              <Button
                  variant="contained"
                  endIcon={<Send />}
                  onClick={updateManagedAccount}
                  disabled={!isFormValid}
                  sx={{
                    backgroundColor: WSU_RED,
                    color: "white",
                    px: 4,
                    py: 1.25,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    "&:hover": { backgroundColor: "#8a0c25" },
                    "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
                  }}
              >
                Update Account
              </Button>
            </Box>

          </TableContainer>
        </Box>
      </ThemeProvider>
  );
};